import React, { useEffect, useMemo, useState } from 'react'
import { Loader2, Search, ArrowUpRight, Edit3, ShieldAlert, CheckCircle, Ban, FileText } from 'lucide-react'
import { apiClient } from '@/lib/api'

type TabKey = 'positions' | 'candidates' | 'recruiters'

const tabs: { key: TabKey; label: string; description: string }[] = [
  { key: 'positions', label: 'Positions', description: 'Review, approve, or reject positions shared with admin.' },
  { key: 'candidates', label: 'Candidates', description: 'Resolve candidate escalations and oversee shared pools.' },
  { key: 'recruiters', label: 'Recruiters', description: 'Verify recruiter identity documents and approvals.' }
]

const quickActions = {
  positions: [
    { title: 'Pending Reviews', href: '#pending', note: 'Show all positions waiting for admin decision.' },
    { title: 'Search Positions', href: '#search', note: 'Search and open any position (created or shared).' },
    { title: 'Position Review Links', href: '#list', note: 'Open review URLs identical to email links.' }
  ],
  candidates: [
    { title: 'Candidate Directory', href: '/recruiter/candidates', note: 'Browse or search shared candidate profiles.' },
    { title: 'Applications', href: '/recruiter/candidates?tab=applications', note: 'Review applications tied to shared positions.' },
    { title: 'Messages', href: '/recruiter/messages', note: 'Follow up on candidate communication threads.' }
  ],
  recruiters: [
    { title: 'ID Submissions', href: '/recruiter/settings', note: 'Check recruiter identity submissions for approval.' },
    { title: 'Team Access', href: '/recruiter/teams', note: 'Confirm team membership for shared access control.' },
    { title: 'Admin Applicants', href: '/admin/recruiters/applicants', note: 'View applicants awaiting recruiter approval.' }
  ]
} as const

const infoBlocks = {
  positions: [
    'Positions visible here include those you created, shared directly with you, or shared with your teams.',
    'Use the recruiter console to manage publishing status; admin review links stay under /admin/positions/{id}/review.',
    'Pending and rejected roles remain hidden from the recruiter list until approved or reopened.'
  ],
  candidates: [
    'Leverage search and filters in the recruiter candidate views to surface shared pools.',
    'Escalations or flagged candidates should be handled via direct outreach in Messages.',
    'Cross-check candidate access with position sharing to ensure correct visibility.'
  ],
  recruiters: [
    'Verify government ID uploads before approving recruiter mode.',
    'Use team membership to grant position visibility to groups instead of broad sharing.',
    'Keep approvals and holds in sync with identity document status.'
  ]
} as const

export default function AdminConsolePage() {
  const [activeTab, setActiveTab] = useState<TabKey>('positions')
  const [positionsLoading, setPositionsLoading] = useState(false)
  const [positionError, setPositionError] = useState<string | null>(null)
  const [positions, setPositions] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'pending_review' | 'all'>('pending_review')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [candidatesLoading, setCandidatesLoading] = useState(false)
  const [candidateError, setCandidateError] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<any[]>([])
  const [candidateSearch, setCandidateSearch] = useState('')
  const [candidatePage, setCandidatePage] = useState(1)
  const [candidatePageSize] = useState(20)
  const [candidateTotalPages, setCandidateTotalPages] = useState(1)
  const [recruiterApplicants, setRecruiterApplicants] = useState<any[]>([])
  const [recruiterLoading, setRecruiterLoading] = useState(false)
  const [recruiterError, setRecruiterError] = useState<string | null>(null)
  const [recruiterSearch, setRecruiterSearch] = useState('')
  const [recruiterStatusFilter, setRecruiterStatusFilter] = useState<'all' | 'pending_review' | 'approved' | 'hold'>('all')

  const fetchPositions = async (overrideStatus?: 'pending_review' | 'all', overridePage?: number) => {
    const status = overrideStatus || statusFilter
    const nextPage = overridePage || page
    try {
      setPositionsLoading(true)
      setPositionError(null)

      const params: Record<string, string> = {
        limit: String(pageSize),
        page: String(nextPage),
        include_shared: 'true'
      }
      if (status === 'pending_review') params.status = 'pending_review'
      if (searchTerm.trim()) params.search = searchTerm.trim()

      const response = await apiClient.getPositions(params)

      if (response.success && response.data) {
        const data: any = response.data
        const buckets: any[] = []

        if (Array.isArray(data)) buckets.push(data)

        const keys = ['positions', 'shared_positions', 'team_shared_positions', 'sharedWithMe', 'shared_with_me', 'teamShared', 'team_shared']
        keys.forEach(key => {
          const value = data?.[key]
          if (Array.isArray(value)) buckets.push(value)
        })

        const merged = buckets.flat()
        const unique = new Map<string, any>()
        merged.forEach((p: any) => {
          const id = (p.uuid || p.id || '').toString()
          if (id && !unique.has(id)) {
            unique.set(id, p)
          }
        })

        const normalized = Array.from(unique.values()).map((p: any) => ({
          id: (p.uuid || p.id || '').toString(),
          title: p.title || 'Untitled',
          company: p.company_name || p.company || 'Unknown',
          status: (p.status || 'unknown').toString(),
          createdAt: p.created_at || p.createdAt || p.datePosted,
          shareType: p.share_type || p.shareType || 'created',
          sharedTeamName: p.shared_team_name || p.sharedTeamName,
          sharedByName: p.shared_by_name || p.sharedByName,
          accessReason: p.access_reason || p.accessReason,
        }))

        setPositions(normalized)

        // derive pagination
        const pagination = data?.pagination
        if (pagination) {
          setTotalPages(pagination.total || 1)
        } else {
          const inferredTotal = Math.max(1, Math.ceil((data?.totalItems || normalized.length) / pageSize))
          setTotalPages(inferredTotal)
        }
        setPage(nextPage)
      } else {
        setPositions([])
        setPositionError('Unable to load positions')
        setTotalPages(1)
      }
    } catch (err: any) {
      console.error('Failed to load positions', err)
      setPositionError(err?.message || 'Failed to load positions')
      setPositions([])
      setTotalPages(1)
    } finally {
      setPositionsLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'positions') {
      fetchPositions(undefined, page)
    }
    if (activeTab === 'candidates') {
      fetchCandidates(candidatePage)
    }
    if (activeTab === 'recruiters') {
      fetchRecruiterApplicants()
    }
  }, [activeTab, statusFilter, page, candidatePage])

  const pendingPositions = useMemo(() => positions.filter(p => (p.status || '').toLowerCase() === 'pending_review'), [positions])
  const displayedCandidates = useMemo(() => {
    if (!candidateSearch.trim()) return candidates
    const needle = candidateSearch.toLowerCase()
    return candidates.filter((c) => {
      const name = (c.name || '').toLowerCase()
      const email = (c.email || '').toLowerCase()
      return name.includes(needle) || email.includes(needle)
    })
  }, [candidates, candidateSearch])

  const fetchCandidates = async (overridePage?: number) => {
    const nextPage = overridePage || candidatePage
    try {
      setCandidatesLoading(true)
      setCandidateError(null)

      const params: Record<string, string> = {
        limit: String(candidatePageSize),
        page: String(nextPage),
        include_shared: 'true'
      }
      if (candidateSearch.trim()) params.search = candidateSearch.trim()

      const response = await apiClient.getCandidates(params)

      if (response.success && response.data) {
        const data: any = response.data
        const list = Array.isArray(data) ? data : data.candidates
        const normalized = Array.isArray(list)
          ? list.map((c: any) => ({
              id: (c.uuid || c.id || '').toString(),
              name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unnamed',
              email: c.email || c.email_id,
              location: c.location || c.current_location || '—',
              currentPosition: c.current_position || c.currentPosition || c.title,
              status: c.status || 'active'
            })).filter((c: any) => c.id)
          : []

        setCandidates(normalized)

        const pagination = data?.pagination
        if (pagination) {
          setCandidateTotalPages(pagination.total || 1)
        } else {
          const inferredTotal = Math.max(1, Math.ceil((data?.totalItems || normalized.length) / candidatePageSize))
          setCandidateTotalPages(inferredTotal)
        }
        setCandidatePage(nextPage)
      } else {
        setCandidates([])
        setCandidateError('Unable to load candidates')
        setCandidateTotalPages(1)
      }
    } catch (err: any) {
      console.error('Failed to load candidates', err)
      setCandidateError(err?.message || 'Failed to load candidates')
      setCandidates([])
      setCandidateTotalPages(1)
    } finally {
      setCandidatesLoading(false)
    }
  }

  const fetchRecruiterApplicants = async () => {
    try {
      setRecruiterLoading(true)
      setRecruiterError(null)

      const params: Record<string, string> = {}
      if (recruiterSearch.trim()) params.search = recruiterSearch.trim()
      if (recruiterStatusFilter !== 'all') params.status = recruiterStatusFilter

      const response = await apiClient.getAdminRecruiterApplicants(params)
      if (response.success) {
        const data: any = response.data || {}
        const list = Array.isArray(data)
          ? data
          : data.items || data.applicants || data.results || []

        const normalized = Array.isArray(list)
          ? list.map((item: any) => {
              const user = item.user || {}
              const uuid = item.user_uuid || user.uuid || item.uuid
              const firstName = item.first_name || user.first_name || user.firstName || ''
              const lastName = item.last_name || user.last_name || user.lastName || ''
              return {
                uuid,
                name: `${firstName} ${lastName}`.trim() || 'Unknown',
                email: item.email || user.email,
                status: item.status || item.application_status || user.recruiter_status || 'pending_review',
                uploadedAt: item.recruiter_id_uploaded_at || item.identity_document_uploaded_at || item.created_at || item.createdAt,
                documentUuid: item.application_uuid || item.identity_document_uuid || item.document_uuid,
                documentKey: item.recruiter_id_s3_key || item.s3_key || item.document_s3_key || item.document_key,
                documentType: item.recruiter_id_mime_type || item.mime_type || item.content_type || item.document_mime_type,
              }
            }).filter((r: any) => r.uuid)
          : []

        setRecruiterApplicants(normalized)
        if (!Array.isArray(list)) {
          const count = Array.isArray(normalized) ? normalized.length : 0
          if (count === 0) {
            setRecruiterError('No recruiter applicants found')
          }
        }
      } else {
        setRecruiterApplicants([])
        setRecruiterError(response.error || 'Unable to load recruiter applicants')
      }
    } catch (err: any) {
      console.error('Failed to load recruiter applicants', err)
      setRecruiterError(err?.message || 'Failed to load recruiter applicants')
      setRecruiterApplicants([])
    } finally {
      setRecruiterLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-indigo-600">Admin Console</p>
            <h1 className="mt-1 text-2xl font-semibold text-gray-900">Administration</h1>
            <p className="mt-2 text-sm text-gray-600">Review positions, candidates, and recruiter approvals from one place.</p>
          </div>
        </header>

        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200 flex flex-wrap">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key)
                  if (tab.key === 'candidates') {
                    setCandidatePage(1)
                    fetchCandidates(1)
                  }
                  if (tab.key === 'positions') {
                    setPage(1)
                    fetchPositions(statusFilter, 1)
                  }
                }}
                className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.key
                    ? 'border-indigo-600 text-indigo-700 bg-indigo-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-gray-900">
                {tabs.find(t => t.key === activeTab)?.label}
              </h2>
              <p className="text-sm text-gray-600">{tabs.find(t => t.key === activeTab)?.description}</p>
            </div>
            {activeTab === 'positions' ? (
              <div className="space-y-4" id="list">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div className="flex items-center gap-2" id="pending">
                    <button
                      onClick={() => { setStatusFilter('pending_review'); fetchPositions('pending_review') }}
                    className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border ${statusFilter === 'pending_review' ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    <ShieldAlert className="w-4 h-4 mr-2" /> Pending review ({pendingPositions.length})
                  </button>
                  <button
                      onClick={() => { setStatusFilter('all'); setPage(1); fetchPositions('all', 1) }}
                      className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border ${statusFilter === 'all' ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    <ArrowUpRight className="w-4 h-4 mr-2" /> All positions
                  </button>
                  </div>

                  <form
                    id="search"
                    className="flex items-center gap-2 w-full lg:w-auto"
                    onSubmit={(e) => { e.preventDefault(); setPage(1); fetchPositions(undefined, 1) }}
                  >
                    <div className="relative flex-1 lg:w-80">
                      <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full rounded-md border border-gray-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Search positions by title or description"
                      />
                    </div>
                    <button
                      type="submit"
                      className="inline-flex items-center px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                    >
                      Search
                    </button>
                  </form>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                  {positionsLoading ? (
                    <div className="flex items-center justify-center py-10 space-x-3 text-gray-600">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Loading positions...</span>
                    </div>
                  ) : positionError ? (
                    <div className="py-6 px-4 text-sm text-red-600">{positionError}</div>
                  ) : positions.length === 0 ? (
                    <div className="py-6 px-4 text-sm text-gray-600">No positions found for this view.</div>
                  ) : (
                    positions.map((p) => {
                      const status = (p.status || '').toLowerCase()
                      const isPending = status === 'pending_review'
                      return (
                        <div key={p.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-gray-900">{p.title}</p>
                              <span className={`text-xs px-2 py-1 rounded-full ${isPending ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : 'bg-gray-100 text-gray-700'}`}>
                                {p.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{p.company}</p>
                            <p className="text-xs text-gray-500">
                              {p.sharedTeamName || p.sharedByName ? `${p.sharedTeamName || p.sharedByName} · ${p.shareType}` : p.shareType}
                              {p.accessReason ? ` · ${p.accessReason}` : ''}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <a
                              href={`/admin/positions/${p.id}/review`}
                              className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                            >
                              <ShieldAlert className="w-4 h-4 mr-2" /> Review
                            </a>
                            <a
                              href={`/recruiter/positions/${p.id}`}
                              className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
                            >
                              <Edit3 className="w-4 h-4 mr-2" /> Edit
                            </a>
                            <a
                              href={`/recruiter/positions/${p.id}`}
                              className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
                            >
                              <ArrowUpRight className="w-4 h-4 mr-2" /> Open
                            </a>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>

                <div className="flex items-center justify-between text-sm text-gray-700">
                  <div>
                    Page {page} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={page <= 1 || positionsLoading}
                      className="px-3 py-2 rounded-md border border-gray-200 bg-white disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={page >= totalPages || positionsLoading}
                      className="px-3 py-2 rounded-md border border-gray-200 bg-white disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>

                <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-2">
                  <p className="text-sm font-semibold text-gray-800">What to keep in mind</p>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    {infoBlocks[activeTab].map(point => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : activeTab === 'candidates' ? (
              <div className="space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <form
                    className="flex items-center gap-2 w-full lg:w-auto"
                    onSubmit={(e) => { e.preventDefault(); setCandidatePage(1); fetchCandidates(1) }}
                  >
                    <div className="relative flex-1 lg:w-80">
                      <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={candidateSearch}
                        onChange={(e) => setCandidateSearch(e.target.value)}
                        className="w-full rounded-md border border-gray-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Search candidates by name or email"
                      />
                    </div>
                    <button
                      type="submit"
                      className="inline-flex items-center px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                    >
                      Search
                    </button>
                  </form>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                  {candidatesLoading ? (
                    <div className="flex items-center justify-center py-10 space-x-3 text-gray-600">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Loading candidates...</span>
                    </div>
                  ) : candidateError ? (
                    <div className="py-6 px-4 text-sm text-red-600">{candidateError}</div>
                  ) : displayedCandidates.length === 0 ? (
                    <div className="py-6 px-4 text-sm text-gray-600">No candidates found for this view.</div>
                  ) : (
                    displayedCandidates.map((c) => (
                      <div key={c.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-gray-900">{c.name}</p>
                          <p className="text-sm text-gray-600">{c.email || 'No email'}</p>
                          <p className="text-xs text-gray-500">{c.location} · {c.currentPosition || 'Candidate'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`/recruiter/candidates/${c.id}`}
                            className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
                          >
                            <ArrowUpRight className="w-4 h-4 mr-2" /> View profile
                          </a>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="flex items-center justify-between text-sm text-gray-700">
                  <div>
                    Page {candidatePage} of {candidateTotalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCandidatePage((prev) => Math.max(1, prev - 1))}
                      disabled={candidatePage <= 1 || candidatesLoading}
                      className="px-3 py-2 rounded-md border border-gray-200 bg-white disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCandidatePage((prev) => Math.min(candidateTotalPages, prev + 1))}
                      disabled={candidatePage >= candidateTotalPages || candidatesLoading}
                      className="px-3 py-2 rounded-md border border-gray-200 bg-white disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>

                <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-2">
                  <p className="text-sm font-semibold text-gray-800">What to keep in mind</p>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    {infoBlocks[activeTab].map(point => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  {quickActions[activeTab].map(action => (
                    <a
                      key={action.title}
                      href={action.href}
                      className="block rounded-lg border border-gray-200 p-4 hover:border-indigo-200 hover:shadow-sm transition"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{action.title}</p>
                          <p className="mt-1 text-sm text-gray-600">{action.note}</p>
                        </div>
                        <span className="text-indigo-600 text-sm font-medium">Open →</span>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-2">
                  <p className="text-sm font-semibold text-gray-800">What to keep in mind</p>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    {infoBlocks[activeTab].map(point => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>

        {activeTab === 'recruiters' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Recruiter applications</h2>
                <p className="text-sm text-gray-600">Search, view ID uploads, and approve or hold recruiter access.</p>
              </div>
              <div className="flex gap-2">
                <input
                  value={recruiterSearch}
                  onChange={(e) => setRecruiterSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchRecruiterApplicants() }}
                  placeholder="Search name or email"
                  className="w-52 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <select
                  value={recruiterStatusFilter}
                  onChange={(e) => setRecruiterStatusFilter(e.target.value as any)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All statuses</option>
                  <option value="pending_review">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="hold">On hold</option>
                </select>
                <button
                  onClick={fetchRecruiterApplicants}
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                >
                  <Search className="w-4 h-4" /> Refresh
                </button>
              </div>
            </div>

            {recruiterLoading ? (
              <div className="flex items-center justify-center py-10 text-sm text-gray-600">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading applicants...
              </div>
            ) : recruiterError ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{recruiterError}</div>
            ) : recruiterApplicants.length === 0 ? (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">No recruiter applications found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase text-gray-500">
                      <th className="px-3 py-2">Applicant</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Uploaded</th>
                      <th className="px-3 py-2">Document</th>
                      <th className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recruiterApplicants
                      .filter(a => recruiterStatusFilter === 'all' || (a.status || '').toLowerCase() === recruiterStatusFilter)
                      .filter(a => {
                        if (!recruiterSearch.trim()) return true
                        const needle = recruiterSearch.toLowerCase()
                        return (a.name || '').toLowerCase().includes(needle) || (a.email || '').toLowerCase().includes(needle)
                      })
                      .map(applicant => (
                        <tr key={applicant.uuid} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-3">
                            <div className="font-medium text-gray-900">{applicant.name || 'Unknown'}</div>
                            <div className="text-xs text-gray-600">{applicant.email || '—'}</div>
                          </td>
                          <td className="px-3 py-3">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                              applicant.status === 'approved'
                                ? 'bg-green-100 text-green-700'
                                : applicant.status === 'hold'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-blue-100 text-blue-700'
                            }`}>
                              {applicant.status === 'approved' ? <CheckCircle className="w-3 h-3" /> : applicant.status === 'hold' ? <ShieldAlert className="w-3 h-3" /> : <Loader2 className="w-3 h-3" />} 
                              {applicant.status?.replace('_', ' ') || 'pending_review'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-700">{applicant.uploadedAt ? new Date(applicant.uploadedAt).toLocaleString() : '—'}</td>
                          <td className="px-3 py-3 text-sm text-gray-700">
                            {applicant.documentKey ? (
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-gray-500" />
                                <div className="flex flex-col">
                                  <span className="text-xs text-gray-900">{applicant.documentKey}</span>
                                  <span className="text-[11px] text-gray-500">{applicant.documentType || 'unknown type'}</span>
                                </div>
                              </div>
                            ) : '—'}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => apiClient.updateRecruiterStatus(applicant.uuid, 'approved').then(fetchRecruiterApplicants)}
                                className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
                              >
                                <CheckCircle className="w-3 h-3" /> Approve
                              </button>
                              <button
                                onClick={() => apiClient.updateRecruiterStatus(applicant.uuid, 'hold').then(fetchRecruiterApplicants)}
                                className="inline-flex items-center gap-1 rounded-md bg-yellow-500 px-3 py-1 text-xs font-medium text-white hover:bg-yellow-600"
                              >
                                <ShieldAlert className="w-3 h-3" /> Hold
                              </button>
                              <button
                                onClick={() => apiClient.updateRecruiterStatus(applicant.uuid, 'pending_review').then(fetchRecruiterApplicants)}
                                className="inline-flex items-center gap-1 rounded-md bg-gray-200 px-3 py-1 text-xs font-medium text-gray-800 hover:bg-gray-300"
                              >
                                <Loader2 className="w-3 h-3" /> Reset
                              </button>
                              <button
                                onClick={() => apiClient.messageRecruiterApplicant(applicant.uuid, { message: 'Your recruiter access has been blocked.' }).then(fetchRecruiterApplicants)}
                                className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                              >
                                <Ban className="w-3 h-3" /> Block
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
